import React from 'react';
import { Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { MODULES_CONDUITE } from '../data/modulesConduite';
import { Cog, Eye, ScanEye, GitFork, RotateCw, Clock, ArrowRight, Sparkles, BookOpen, Lock } from 'lucide-react';
import useMetaTags from '../lib/useMetaTags';

const ICONS = { Cog, Eye, ScanEye, GitFork, RotateCw };

const COLOR_MAP = {
  red:     { border: 'border-red-500/40',     bg: 'bg-red-950/25',     text: 'text-red-300',     ring: 'hover:border-red-400',     btn: 'bg-red-600 hover:bg-red-500' },
  amber:   { border: 'border-amber-500/40',   bg: 'bg-amber-950/25',   text: 'text-amber-300',   ring: 'hover:border-amber-400',   btn: 'bg-amber-600 hover:bg-amber-500' },
  sky:     { border: 'border-sky-500/40',     bg: 'bg-sky-950/25',     text: 'text-sky-300',     ring: 'hover:border-sky-400',     btn: 'bg-sky-600 hover:bg-sky-500' },
  emerald: { border: 'border-emerald-500/40', bg: 'bg-emerald-950/25', text: 'text-emerald-300', ring: 'hover:border-emerald-400', btn: 'bg-emerald-600 hover:bg-emerald-500' },
  fuchsia: { border: 'border-fuchsia-500/40', bg: 'bg-fuchsia-950/25', text: 'text-fuchsia-300', ring: 'hover:border-fuchsia-400', btn: 'bg-fuchsia-600 hover:bg-fuchsia-500' },
};

function ModuleCard({ m }) {
  const Icon = ICONS[m.icon] || Cog;
  const c = COLOR_MAP[m.color] || COLOR_MAP.red;
  const isActive = m.status === 'active';

  const CardInner = (
    <div className={`rounded-2xl border ${c.border} ${c.bg} ${isActive ? c.ring : 'opacity-70'} p-5 sm:p-6 transition relative overflow-hidden h-full flex flex-col`} data-testid={`module-card-${m.slug}`}>
      <div className="absolute -top-4 -right-4 opacity-10 pointer-events-none">
        <Icon size={140} strokeWidth={1.2} />
      </div>
      <div className="relative flex-1 flex flex-col">
        <div className="flex items-center justify-between">
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${c.border} ${c.text} text-[10.5px] font-mono-spec uppercase tracking-wider font-bold`}>
            <Icon size={11} /> {m.code}
          </div>
          <div className="text-white/40 text-[10.5px] font-mono-spec inline-flex items-center gap-1"><Clock size={10} /> {m.duree}</div>
        </div>
        <h3 className="mt-4 font-serif-editorial text-white text-[22px] sm:text-[24px] leading-tight">{m.title}</h3>
        <p className="mt-1.5 text-white/60 text-[13.5px] leading-snug">{m.subtitle}</p>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/15 bg-white/[0.03] text-white/70 text-[10.5px] font-mono-spec`}><BookOpen size={9} /> {m.remc_lecon}</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/15 bg-white/[0.03] text-white/60 text-[10.5px] font-mono-spec">{m.difficulty}</span>
        </div>

        <p className="mt-4 text-white/70 text-[13px] leading-relaxed flex-1">{m.description}</p>

        <div className="mt-5">
          {isActive ? (
            <Link
              to={`/modules-conduite/${m.slug}`}
              data-testid={`module-open-${m.slug}`}
              className={`inline-flex items-center gap-2 h-11 px-5 rounded-full ${c.btn} text-white text-[13px] font-bold transition w-full justify-center shadow-lg`}
            >
              Ouvrir le module <ArrowRight size={13} />
            </Link>
          ) : (
            <div className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/[0.03] text-white/50 text-[13px] font-semibold w-full justify-center cursor-not-allowed" data-testid={`module-locked-${m.slug}`}>
              <Lock size={13} /> Bientôt disponible
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return CardInner;
}

export default function ModulesConduiteLandingPage() {
  useMetaTags({
    title: 'Modules interactifs — Cours en voiture — Code Voiture',
    description: 'Schémas SVG interactifs, simulateurs et études de cas pour les points techniques clés de la conduite : chaîne cinématique, angles morts, indices, intersections, giratoires.',
    ogUrl: 'https://codevoiture.fr/modules-conduite',
    ogType: 'website',
  });

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 py-10 sm:py-14">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Cours en voiture · Modules interactifs</span>
        </nav>

        <SectionTitle
          kicker="Cours en voiture · Compléments interactifs"
          title="Les modules qu'on ne peut pas expliquer sans schéma"
        />
        <p className="mt-4 text-white/70 text-[15.5px] max-w-3xl leading-relaxed">
          Chaque module apporte une couche <span className="text-white">visuelle et manipulatoire</span> aux leçons REMC les plus techniques.
          Idéal pour préparer une séance à domicile, projeter en salle ou animer un point difficile pendant le cours en voiture.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3 text-[11.5px] text-white/50 font-mono-spec">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5"><Sparkles size={11} /> SVG interactifs faits main</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5">Vidéos YouTube en appui</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5">QCM d'ancrage</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5">Études de cas scénarisées</span>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="modules-grid">
          {MODULES_CONDUITE.map((m) => (
            <ModuleCard key={m.slug} m={m} />
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-7 text-center" data-testid="modules-cta-remc">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/15 bg-white/5 text-white/70 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <BookOpen size={11} /> Complémentaire aux 32 leçons REMC
          </div>
          <h3 className="mt-3 font-serif-editorial text-white text-[22px] sm:text-[26px] leading-tight">Ces modules NE remplacent PAS la trame REMC</h3>
          <p className="mt-2 text-white/60 text-[13.5px] max-w-2xl mx-auto leading-relaxed">
            Ils la complètent avec des visuels difficiles à faire à la main. La trame complète (Pourquoi · Comment · Risques · Influences · Pressions) reste dans <Link to="/cours/eleve/voiture" className="text-red-400 hover:text-red-300 font-semibold">Cours en voiture — 32 leçons</Link>.
          </p>
        </div>
      </section>
    </PageShell>
  );
}
